import type { CustomerResponse } from '../../repositories/account-repo/schemas/customer.zod';

export class Customer {
  private readonly AU = 'AU';

  private readonly NZ = 'NZ';

  private readonly US = 'US';

  private readonly UK = 'GB';

  private readonly CA = 'CA';

  constructor(private apiResponse: CustomerResponse) {}

  // Basic Info Getters
  public getCustomerId() {
    return this.apiResponse.customerId;
  }

  public getEncodedAccountId() {
    return this.apiResponse.encodedAccountId;
  }

  public getFirstName() {
    return this.apiResponse.firstName;
  }

  public getLastName() {
    return this.apiResponse.lastName;
  }

  public getFullName() {
    return `${this.apiResponse.firstName} ${this.apiResponse.lastName}`.trim();
  }

  public getCommunityScreenName() {
    return this.apiResponse.communityScreenName;
  }

  // Contact Info Getters
  public getContactEmail() {
    return this.apiResponse.contactEmail;
  }

  public getContactPhone() {
    return this.apiResponse.contactPhone;
  }

  public getPhonePrefix() {
    return this.apiResponse.phonePrefix;
  }

  public getPhoneRemainder() {
    return this.apiResponse.phoneRemainder;
  }

  public getFormattedPhone() {
    return `${this.apiResponse.phonePrefix}${this.apiResponse.phoneRemainder}`;
  }

  public getOrganizationPhone() {
    return this.apiResponse.organizationPhone;
  }

  // Address Getters
  public getAddressLine1() {
    return this.apiResponse.physicalAddress?.addressLine1 || '';
  }

  public getAddressLine2() {
    return this.apiResponse.physicalAddress?.addressLine2 || '';
  }

  public getAddressLine3() {
    return this.apiResponse.physicalAddress?.addressLine3 || '';
  }

  public getCity() {
    return this.apiResponse.physicalAddress?.city || '';
  }

  public getStateCode() {
    return this.apiResponse.physicalAddress?.stateCode || '';
  }

  public getStateName() {
    return this.apiResponse.physicalAddress?.stateName || '';
  }

  public getPostalCode() {
    return this.apiResponse.physicalAddress?.postalCode || '';
  }

  public getCountryCode() {
    return this.apiResponse.countryCode?.toUpperCase() || '';
  }

  public isUs() {
    return this.getCountryCode() === this.US;
  }

  public isAu() {
    return this.getCountryCode() === this.AU;
  }

  public isNz() {
    return this.getCountryCode() === this.NZ;
  }

  public isUk() {
    return this.getCountryCode() === this.UK;
  }

  public isCa() {
    return this.getCountryCode() === this.CA;
  }

  public getPhysicalAddressCountryCode() {
    return this.apiResponse.physicalAddress?.countryCode?.toUpperCase() || '';
  }

  // Organization Info Getters
  public getOrganizationName() {
    return this.apiResponse.organizationName;
  }

  public getWebsite() {
    return this.apiResponse.website;
  }

  // Account Settings Getters
  public getTimeZoneId() {
    return this.apiResponse.timeZoneId;
  }

  public getTimeZoneStatus() {
    return this.apiResponse.timeZoneStatus;
  }

  // Status Info Getters
  public getDerelict() {
    return this.apiResponse.derelict;
  }

  public getInMaintenanceMode() {
    return this.apiResponse.inMaintenanceMode;
  }

  public getConfirmedOptIn() {
    return this.apiResponse.confirmedOptIn;
  }

  public getCanadianAntiSpamLevel() {
    return this.apiResponse.canadianAntiSpamLevel;
  }

  // Demographics Getters
  public getBusinessType() {
    return this.apiResponse.demographics.businessType;
  }

  public getBusinessTypeFreetext() {
    return this.apiResponse.demographics.businessTypeFreetext;
  }

  // Audit Info Getters
  public getCreatedOn() {
    return this.apiResponse.audit?.createdOn ?? '';
  }

  public getCreatedBy() {
    return this.apiResponse.audit?.createdBy ?? null;
  }

  public getModifiedOn() {
    return this.apiResponse.audit?.modifiedOn ?? '';
  }

  public getModifiedBy() {
    return this.apiResponse.audit?.modifiedBy ?? null;
  }

  // Partner Info Getters
  public getPartnerId() {
    return this.apiResponse.partnerId;
  }

  public getPartnerAccountId() {
    return this.apiResponse.partnerAccountId;
  }

  // Business Logic Methods
  public getIsActive() {
    return !this.apiResponse.derelict;
  }

  public getIsInMaintenanceMode() {
    return this.apiResponse.inMaintenanceMode;
  }

  public getHasConfirmedOptIn() {
    return this.apiResponse.confirmedOptIn;
  }

  public getCustomerAddress() {
    return {
      address1: this.getAddressLine1(),
      address2: this.getAddressLine2(),
      address3: this.getAddressLine3(),
      city: this.getCity(),
      state: this.getStateCode(),
      stateName: this.getStateName(),
      zipcode: this.getPostalCode(),
      country: this.getPhysicalAddressCountryCode(),
    };
  }

  public getDemographics() {
    return {
      businessType: this.getBusinessType(),
      businessTypeFreetext: this.getBusinessTypeFreetext(),
    };
  }

  public getAuditInfo() {
    return {
      createdOn: this.getCreatedOn(),
      createdBy: this.getCreatedBy(),
      modifiedOn: this.getModifiedOn(),
      modifiedBy: this.getModifiedBy(),
    };
  }

  public getCanUseCountryAsBillingInfoCountryFallback() {
    // For Australia, New Zealand, United Kingdom, and Canada we want to allow the country code to be used as a fallback for billing info.
    // https://ctct.atlassian.net/browse/CPD-12246
    return this.isAu() || this.isNz() || this.isUk() || this.isCa();
  }

  public getBillingInfoCountryFallback() {
    return this.getCanUseCountryAsBillingInfoCountryFallback() ? this.getCountryCode() : null;
  }
}
